import type { Margin } from '@visx/xychart';

import { ZOOM_MIN_DRAG_DISTANCE_PX } from '../../../constants';

import styles from '../../../utils/useDragToZoom.module.css';

interface SelectionRectProps {
  /** Whether drag-to-zoom is disabled */
  disableDragToZoom: boolean;
  /** Whether currently dragging */
  isDragging: boolean;
  /** Starting X position of selection in pixels */
  selectionStartX: number | null;
  /** Ending X position of selection in pixels */
  selectionEndX: number | null;
  /** Inner height of the chart */
  innerHeight: number | undefined;
  /** Margin configuration from visx */
  margin: Margin | undefined;
}

/**
 * Component that renders the selection rectangle while dragging to zoom.
 * Returns null if selection should not be displayed.
 */
export function SelectionRect({
  disableDragToZoom,
  isDragging,
  selectionStartX,
  selectionEndX,
  innerHeight,
  margin,
}: SelectionRectProps): React.ReactNode {
  if (
    disableDragToZoom ||
    !isDragging ||
    selectionStartX === null ||
    selectionEndX === null ||
    !innerHeight ||
    !margin
  ) {
    return null;
  }

  const dragDistance = Math.abs(selectionEndX - selectionStartX);
  if (dragDistance < ZOOM_MIN_DRAG_DISTANCE_PX) {
    return null;
  }

  const left = Math.min(selectionStartX, selectionEndX);
  const width = Math.abs(selectionEndX - selectionStartX);
  // Handle margin as both object and number
  const top =
    typeof margin === 'object' && margin !== null ? (margin.top ?? 0) : typeof margin === 'number' ? margin : 0;
  const height = innerHeight;

  return (
    <rect
      x={left}
      y={top}
      width={width}
      height={height}
      fill="var(--lp-color-bg-feedback-primary)"
      opacity={0.2}
      stroke="var(--lp-color-border-ui-primary)"
      strokeWidth={1}
      pointerEvents="none"
      className={styles.selectionRect}
    />
  );
}
